import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
  FLYABILITY_PRESETS,
  type FlyabilityMetric,
  type SkillPreset,
} from "../features/flyability/flyability";

const Container = styled.div`
  width: 100%;
  max-width: 820px;
  margin: 1em auto 4em;
  padding: 0 1.5em;
  line-height: 1.6;
`;

const Back = styled(Link)`
  display: inline-block;
  margin-bottom: 0.5em;
  font-size: 0.85em;
  opacity: 0.7;
  text-decoration: underline;
`;

const Lede = styled.p`
  font-size: 1.05em;
  opacity: 0.92;
`;

const Section = styled.section`
  margin-top: 2em;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.75em;
  font-size: 0.92em;
  font-variant-numeric: tabular-nums;

  th,
  td {
    border: 1px solid rgba(255, 255, 255, 0.12);
    padding: 7px 10px;
    text-align: center;
  }
  th {
    background: rgba(255, 255, 255, 0.05);
    font-weight: 600;
  }
  td:first-of-type,
  th:first-of-type {
    text-align: left;
  }
`;

const Caption = styled.p`
  font-size: 0.82em;
  opacity: 0.6;
  margin-top: 0.4em;
`;

const Note = styled.aside`
  margin-top: 1em;
  padding: 0.8em 1em;
  border-left: 3px solid #37c871;
  background: rgba(55, 200, 113, 0.06);
  border-radius: 6px;
  font-size: 0.94em;
`;

const Warn = styled.aside`
  margin-top: 1em;
  padding: 0.8em 1em;
  border-left: 3px solid #e3b341;
  background: rgba(227, 179, 65, 0.07);
  border-radius: 6px;
  font-size: 0.94em;
`;

const METRIC_ROWS: { key: FlyabilityMetric; label: string; unit: string }[] = [
  { key: "windSpeed", label: "Wind", unit: "km/h" },
  { key: "windGust", label: "Gust", unit: "km/h" },
  { key: "precipitationChance", label: "Precipitation", unit: "%" },
  { key: "cloudCover", label: "Cloud cover", unit: "%" },
  { key: "thermal", label: "Thermals", unit: "J/kg" },
];

const LEVELS: SkillPreset[] = ["beginner", "intermediate", "advanced"];
const LEVEL_LABEL: Record<SkillPreset, string> = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};

/**
 * Pilot-grade explanation of how the flyable-window verdict is built — data
 * source, the five scored metrics with their real thresholds, the daylight
 * rule, window grouping, lesson suitability, and the limits of the model.
 */
export default function Methodology() {
  const { t } = useTranslation();
  const tr = t as unknown as (k: string) => string;

  return (
    <Container>
      <Back to="/">{t("← Back")}</Back>
      <h1>{t("How the flyable window is calculated")}</h1>
      <Lede>
        {t(
          "This page documents exactly how the flyable-window verdict is produced — the data, the thresholds, and the assumptions — so you can judge it the way you'd judge any other source before launch. It is decision support, not a clearance: the pilot in command owns the go/no-go call.",
        )}
      </Lede>

      <Section>
        <h2>{t("Data source")}</h2>
        <p>
          {t(
            "Every hour comes from Open-Meteo's hourly forecast, a blend of national weather-model runs interpolated to your exact coordinates and refreshed several times a day. We read sustained wind and gusts at 10 m above ground, precipitation probability, total cloud cover, temperature, and CAPE (convective energy).",
          )}
        </p>
        <p>
          {t(
            "Two things to keep in mind. First, this is a model forecast, not an observation — it carries the usual uncertainty, growing with lead time. Second, the wind is the open-terrain value at 10 m: it is not corrected for your site's relief, tree line, venturi effects, or slope/sea breezes. Treat it as the synoptic backdrop, then apply local knowledge.",
          )}
        </p>
      </Section>

      <Section>
        <h2>{t("The five metrics and their thresholds")}</h2>
        <p>
          {t(
            "Each hour is scored on five metrics. For every metric, a value at or below the \"go\" bound is green; at or below the \"marginal\" bound is amber; beyond it is red. The bounds depend on the skill level you select — more conservative for beginners, more permissive for advanced pilots. Below are the actual bounds in use, shown as go / marginal:",
          )}
        </p>
        <Table>
          <thead>
            <tr>
              <th>{t("Metric")}</th>
              {LEVELS.map((lvl) => (
                <th key={lvl}>{tr(LEVEL_LABEL[lvl])}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {METRIC_ROWS.map((row) => (
              <tr key={row.key}>
                <td>
                  {tr(row.label)} <span style={{ opacity: 0.55 }}>({row.unit})</span>
                </td>
                {LEVELS.map((lvl) => {
                  const b = FLYABILITY_PRESETS[lvl][row.key];
                  return (
                    <td key={lvl}>
                      {b.go} / {b.marginal}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
        <Caption>
          {t(
            "You can override any of these in Settings → Flyability; the badge then reads \"Custom\".",
          )}
        </Caption>
        <p>
          <strong>{t("Why each metric matters")}</strong>
        </p>
        <ul>
          <li>
            {t(
              "Sustained wind sets your penetration and ground-handling margin. Past roughly two-thirds of trim speed you stop making headway and risk going backwards; on the ground, inflations and aborts get harder.",
            )}
          </li>
          <li>
            {t(
              "Gusts are usually the binding constraint, which is why their bounds sit close to the sustained ones. A wing is a low-inertia, lightly-loaded aircraft: a sharp gust spread changes angle of attack and wing loading fast, and is the classic trigger for collapses and hard launches.",
            )}
          </li>
          <li>
            {t(
              "Precipitation probability is a hard limit, not a comfort metric: a wet wing loses performance and can stall unpredictably, and rain usually rides with the instability and gust fronts you want to avoid.",
            )}
          </li>
          <li>
            {t(
              "Cloud cover is read as a proxy for instability and ceiling: heavy mid-day cover often signals convection and shrinking VFR margins rather than a calm overcast.",
            )}
          </li>
          <li>
            {t(
              "Thermal turbulence is covered on its own below.",
            )}
          </li>
        </ul>
      </Section>

      <Section>
        <h2>{t("The worst-metric rule")}</h2>
        <p>
          {t(
            "An hour's overall rating is the worst of its five metrics — never an average. One red flag grounds the hour even if everything else is perfect, because in flying a single limiting factor is enough. When an hour is amber or red, the card names the binding metric (\"limited by gusts\"), so you know what to watch. Missing data is treated as not flyable, never as a green light.",
          )}
        </p>
      </Section>

      <Section>
        <h2>{t("Thermal turbulence (CAPE)")}</h2>
        <p>
          {t(
            "CAPE — Convective Available Potential Energy, in joules per kilogram — measures how much energy a rising parcel of air can release. It is the standard proxy for convective, thermic activity: low CAPE means smooth, stable air; high CAPE means active thermals, stronger updraughts and the bumps, cores and shear between them.",
          )}
        </p>
        <p>
          {t(
            "Paramotors are light and lightly loaded, so they feel thermal turbulence far more than a sailplane or GA aircraft — which is exactly why the calmest air is at dawn and in the evening. We fold CAPE into the verdict so a calm-surface-wind but thermic mid-day hour is correctly flagged marginal or no-go. Bounds run from 100/300 J/kg for beginners to 600/1500 for advanced pilots.",
          )}
        </p>
        <Warn>
          {t(
            "CAPE is a proxy, not a turbulence measurement. It captures convective potential well but says nothing about mechanical turbulence from terrain, rotor, or wind shear — judge those yourself.",
          )}
        </Warn>
      </Section>

      <Section>
        <h2>{t("Daylight is a hard limit")}</h2>
        <p>
          {t(
            "Independent of weather, any hour outside the legal flying window is marked not flyable — from 30 minutes before sunrise to 30 minutes after sunset, the standard daylight-VFR margin for ultralight/paramotor operations. Sunrise and sunset are computed locally for your exact coordinates and date, and the time everywhere on the site is shown in Eastern (America/Toronto).",
          )}
        </p>
      </Section>

      <Section>
        <h2>{t("Windows, the 48-hour bar and the 7-day outlook")}</h2>
        <p>
          {t(
            "Consecutive \"go\" hours are grouped into a flyable window; a single marginal or red hour, or a gap in the data, closes it. The headline names the next window and how soon it opens. The 48-hour bar shows the green/amber/red sequence at a glance with a marker for now, and the 7-day outlook gives each day's best rating and the start of its first window — useful for planning a session two or three days out.",
          )}
        </p>
      </Section>

      <Section>
        <h2>{t("\"Suitable now\": matching conditions to training")}</h2>
        <p>
          {t(
            "The same conditions are translated into what kind of session fits, because the threshold is not the same for every activity:",
          )}
        </p>
        <ul>
          <li>
            {t(
              "Ground handling / kiting wants some wind and is the productive option precisely when it is too strong or gusty to fly — so it can be green while flying is red.",
            )}
          </li>
          <li>
            {t(
              "First flights use the beginner thresholds: the calmest, smoothest, legal air only.",
            )}
          </li>
          <li>
            {t("Local flight follows your selected level's rating.")}
          </li>
          <li>
            {t(
              "Cross-country additionally needs a sustained window of at least three hours of go conditions.",
            )}
          </li>
        </ul>
      </Section>

      <Section>
        <h2>{t("Limits — and the pilot's responsibility")}</h2>
        <p>
          {t(
            "This tool models open-terrain forecast conditions. It does not model rotor downwind of obstacles, terrain and venturi acceleration, slope and valley breezes, the low-level wind gradient, sea/lake effects, or any microclimate — all of which can make a green hour unflyable at your specific site. Forecast skill also degrades with lead time, so a 6-day window is a hint, not a promise.",
          )}
        </p>
        <Note>
          {t(
            "Use the flyable window to narrow down when to look harder, then verify with real observations — a windsock, a wind meter, the sky, and your own read of the site. The final go/no-go decision, and the responsibility for it, are always yours.",
          )}
        </Note>
      </Section>
    </Container>
  );
}
